import { useEffect, useState } from "react";
import { Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

export default function AdminWelcomeBonus() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.welcomeBonus().then(setCfg).catch(() => toast.error("Nie udało się wczytać konfiguracji bonusu."));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminApi.updateWelcomeBonus({
        enabled: cfg.enabled,
        credits: Number(cfg.credits),
        message: cfg.message,
      });
      setCfg((c) => ({ ...c, ...updated }));
      toast.success("Zapisano konfigurację bonusu powitalnego.");
    } catch { toast.error("Nie udało się zapisać."); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-accent" /></div>;

  const preview = (cfg.message || "").replace("{credits}", String(cfg.credits));
  const mismatch = cfg.generateCost != null && Number(cfg.credits) !== cfg.generateCost;

  return (
    <div className="max-w-xl space-y-6" data-testid="admin-welcome-bonus-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-field bg-brand-accent/12 text-brand-accent"><Gift size={22} /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-brand-text">Bonus powitalny</h1>
          <p className="text-sm text-brand-sec">Kredyty gratis przyznawane raz, po weryfikacji e-mail. Kwota i tekst z konfiguracji.</p>
        </div>
      </div>

      <div className="space-y-5 rounded-panel border border-brand-line bg-brand-surface p-5">
        <label className="flex cursor-pointer items-center justify-between">
          <span className="text-sm font-medium text-brand-text">Bonus włączony</span>
          <input data-testid="wb-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))} className="h-5 w-5 accent-brand-accent" />
        </label>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-brand-text">Liczba kredytów</label>
          <input data-testid="wb-credits" type="number" min={0} value={cfg.credits} onChange={(e) => setCfg((c) => ({ ...c, credits: e.target.value }))} className="w-32 rounded-field border border-brand-line bg-brand-bg px-3 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent" />
          {cfg.generateCost != null && (
            <p className={`mt-1.5 text-[12px] ${mismatch ? "text-amber-400" : "text-brand-muted"}`}>
              Pełna generacja promptu kosztuje obecnie <b>{cfg.generateCost}</b> kr.{mismatch ? " Bonus nie pokrywa dokładnie jednej generacji." : " Bonus pokrywa dokładnie jedną generację."}
            </p>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-brand-text">Tekst (użyj {"{credits}"} jako miejsca na liczbę)</label>
          <textarea data-testid="wb-message" rows={2} value={cfg.message} onChange={(e) => setCfg((c) => ({ ...c, message: e.target.value }))} maxLength={300} className="w-full rounded-field border border-brand-line bg-brand-bg px-3 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent" />
          <p className="mt-1.5 text-[12px] text-brand-muted">Podgląd: <span className="text-brand-sec" data-testid="wb-preview">{preview}</span></p>
        </div>

        <button data-testid="wb-save" onClick={save} disabled={saving} className="rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on disabled:opacity-60">
          {saving ? "Zapisywanie…" : "Zapisz"}
        </button>
        <p className="text-[11px] text-brand-muted">Zmiana dotyczy wyłącznie nowych kont. Istniejących sald nie zmieniamy, a bonus nie jest przyznawany ponownie.</p>
      </div>
    </div>
  );
}
